import React, { FC, useState, useEffect } from 'react';
import { ReactComponent as RiderProfileIcon } from 'assets/icons/rider-thumbnail.svg';
import { ReactComponent as ArrowUpIcon } from 'assets/icons/arrow-up-icon.svg';
import { ReactComponent as ArrowDownIcon } from 'assets/icons/arrow-down-circle-icon.svg';
import { UserStatsType } from './types';
import { DateRangeSelector, DateRangeType } from '@cokitchen/cokitchen-components';
import { apiService } from 'utils/api';
import { formatNumber, getUserName } from 'utils/functions';

type Props = {
  user: any;
  onChange: (val: DateRangeType) => void;
  dateRange: DateRangeType;
};

const UserDetailDashboard: FC<Props> = ({ user, dateRange, onChange }) => {
  const [loading, setLoading] = useState(false);
  const [userStats, setUserStats] = useState({} as UserStatsType);

  const getStats = async () => {
    setLoading(true);
    const {
      date: { startDate: start, endDate: end }
    } = dateRange;
    const res = await apiService.get(`user/one/stats/order/${user.id}`, { params: { start, end } });
    setUserStats(res.data);
    setLoading(false);
  };

  useEffect(() => {
    if (user) getStats();
  }, [dateRange]);

  const isLoss = false;

  const profitMargin = {
    profit: {
      styles: 'text-[#46AE31]',
      icon: <ArrowUpIcon />
    },
    loss: {
      styles: 'text-[#E60A2B]',
      icon: <ArrowDownIcon />
    }
  };
  return (
    <div>
      <div className='flex flex-wrap gap-2 justify-between mb-6'>
        <div className='flex justify-start items-center gap-2'>
          <RiderProfileIcon />
          <p className='font-semibold text-2xl'>{getUserName(user)}</p>
        </div>
        <DateRangeSelector defaultRange={dateRange.range} onChange={onChange} showTime />
      </div>
      {loading ? (
        <div className='leading-[134px] text-center w-full bg-[#F5FCF3] mb-4'>
          <p>Loading...</p>
        </div>
      ) : (
        <div className='flex max-md:flex-col gap-2 justify-between bg-[#F5FCF3] items-stretch md:items-start border border-[#DEE1E3] rounded-[8px] px-4 py-4 text-sm mb-4'>
          <div className='md:basis-[20%] py-2 flex justify-center items-center gap-4 border-b md:border-b-0 md:border-r border-[#DEE1E3]'>
            <div>
              <p className='text-[#595959] text-[10px]'>TOTAL ORDERS</p>
              <p className='font-semibold text-xl pt-1 pb-1'>
                {formatNumber(userStats.total_orders, {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                  style: 'decimal'
                })}
                &nbsp; orders
              </p>
              <div className='flex justify-start items-center gap-[2px]'>
                <div>{isLoss ? profitMargin.loss.icon : profitMargin.profit.icon}</div>
                <p>
                  <span
                    className={`mr-[4px] ${
                      isLoss ? profitMargin.loss.styles : profitMargin.profit.styles
                    }`}>
                    -
                  </span>
                  <span className='lowercase'>{dateRange.range}</span>
                </p>
              </div>
            </div>
          </div>
          <div className='md:basis-[20%] py-2 flex justify-center items-center gap-4 border-b md:border-b-0 md:border-r border-[#DEE1E3]'>
            <div>
              <p className='text-[#595959] text-[10px]'>TOTAL SPENT</p>
              <p className='font-semibold text-xl pt-1 pb-1'>
                {formatNumber(userStats?.total_all_amount_spent)}
              </p>
              <div className='flex justify-start items-center gap-[2px]'>
                <div>{isLoss ? profitMargin.loss.icon : profitMargin.profit.icon}</div>
                <p>
                  <span
                    className={`mr-[4px] ${
                      isLoss ? profitMargin.loss.styles : profitMargin.profit.styles
                    }`}>
                    -
                  </span>
                  <span className='lowercase'>{dateRange.range}</span>
                </p>
              </div>
            </div>
          </div>
          <div className='md:basis-[20%] py-2 flex justify-center items-center gap-4 border-b md:border-b-0 md:border-r border-[#DEE1E3]'>
            <div>
              <p className='text-[rgb(89,89,89)] text-[10px]'>AVG. ORDER DURATION</p>
              <p className='font-semibold text-xl pt-1 pb-1'>{userStats?.average_delivery_time}</p>
              <div className='flex justify-start items-center gap-[2px]'>
                <div>{isLoss ? profitMargin.loss.icon : profitMargin.profit.icon}</div>
                <p>
                  <span
                    className={`mr-[4px] ${
                      isLoss ? profitMargin.loss.styles : profitMargin.profit.styles
                    }`}>
                    -
                  </span>
                  <span className='lowercase'>{dateRange.range}</span>
                </p>
              </div>
            </div>
          </div>
          <div className='md:basis-[20%] py-2 flex justify-center items-center gap-4 '>
            <div>
              <p className='text-[#595959] text-[10px]'>
                TOP POLYGON ({userStats.polygon_stat?.name ?? '-'})
              </p>
              <p className='font-semibold text-xl pt-1 pb-1'>
                {userStats.polygon_stat?.num_orders ?? '-'} Orders
              </p>
              <div className='flex justify-start items-center gap-[2px]'>
                <div>{isLoss ? profitMargin.loss.icon : profitMargin.profit.icon}</div>
                <p>
                  <span
                    className={`mr-[4px] ${
                      isLoss ? profitMargin.loss.styles : profitMargin.profit.styles
                    }`}>
                    -
                  </span>
                  <span className='lowercase'>{dateRange.range}</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDetailDashboard;
